import { useEffect, useState } from 'react'
import { MapPin, Loader2, AlertCircle, Check, X } from 'lucide-react'
import { centersAPI, attendanceAPI, authAPI } from '../services/api'

interface GeolocationCaptureProps {
  onCapture: (location: { latitude: number; longitude: number; accuracy: number }) => void
  onCancel: () => void
}

// Calculate distance between two coordinates in meters
function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000 // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export default function GeolocationCapture({ onCapture, onCancel }: GeolocationCaptureProps) {
  const [location, setLocation] = useState<{ latitude: number; longitude: number; accuracy: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isWithinGeofence, setIsWithinGeofence] = useState<boolean | null>(null)
  const [nearestCenter, setNearestCenter] = useState<any>(null)
  const [centers, setCenters] = useState<any[]>([])
  // The employee's own assigned training centre id — the same centre the
  // backend checks in check_geofence(). undefined = still loading,
  // null = no centre assigned.
  const [assignedCentreId, setAssignedCentreId] = useState<number | null | undefined>(undefined)
  const [slowHint, setSlowHint] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [permissionDenied, setPermissionDenied] = useState(false)
  // Admin-controlled: true only while an admin has started geofence enforcement.
  const [enforced, setEnforced] = useState(false)

  useEffect(() => {
    // Load real training centres from the backend so the geofence preview
    // reflects actual data (final geofence enforcement always happens
    // server-side in the check-in/check-out endpoints).
    // centersAPI.list().then((res) => setCenters(res.data.results || res.data)).catch(() => setCenters([]))
    centersAPI.listAll().then((res) => setCenters(res.data.results || res.data)).catch(() => setCenters([]))
    // Whether outside-radius punches are blocked is an admin setting; the server
    // enforces it regardless — this only decides if the button is disabled.
    authAPI.getCurrentUser()
      .then((res) => setAssignedCentreId(res.data?.profile?.training_centre ?? null))
      .catch(() => setAssignedCentreId(null))
    attendanceAPI.getGeofencePolicy().then((res) => setEnforced(!!res.data.is_enforced_now)).catch(() => setEnforced(false))
  }, [])

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser')
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    setPermissionDenied(false)

    // A high-accuracy GPS fix can take a while indoors. Show a "still
    // working" hint after 6s, and give up after 15s with an actionable
    // error instead of spinning forever.
    const slowTimer = setTimeout(() => setSlowHint(true), 6000)

    const handlePosition = (position: GeolocationPosition) => {
      const { latitude, longitude, accuracy } = position.coords
      const currentLocation = { latitude, longitude, accuracy }
      setLocation(currentLocation)
      setIsLoading(false)
      setError(null)
      setSlowHint(false)

      // Measure against the employee's own assigned centre only (not the
      // nearest of all centres) so the preview matches the backend check.
      const assigned = centers.find((c) => Number(c.id) === Number(assignedCentreId))
      if (assigned) {
        const distance = haversineDistance(latitude, longitude, Number(assigned.latitude), Number(assigned.longitude))
        const radius = assigned.geofence_radius_m || 500
        setNearestCenter({ center: assigned, radius, distance })
        setIsWithinGeofence(distance <= radius)
      } else {
        setNearestCenter(null)
        setIsWithinGeofence(null)
      }
    }

    const handleError = (err: GeolocationPositionError) => {
      if (err.code === err.PERMISSION_DENIED) {
        setPermissionDenied(true)
        setError('Location access is blocked for this site. Enable it in your browser/device settings, then reload the page — clicking "Try again" alone won\'t re-open the permission prompt once it has been denied.')
      } else if (err.code === err.POSITION_UNAVAILABLE) {
        setError('Location information is unavailable. Move to an open area and try again.')
      } else if (err.code === err.TIMEOUT) {
        setError('Timed out getting your location. Check that GPS/location services are on and try again.')
      } else {
        setError('An error occurred while retrieving location.')
      }
      setIsLoading(false)
      setSlowHint(false)
    }

    // maximumAge allows a recent cached fix (much faster) instead of always
    // forcing a brand-new high-accuracy reading; timeout stops it hanging.
    const watchId = navigator.geolocation.watchPosition(handlePosition, handleError, {
      enableHighAccuracy: true,
      maximumAge: 10000,
      timeout: 15000,
    })

    return () => {
      navigator.geolocation.clearWatch(watchId)
      clearTimeout(slowTimer)
    }
  }, [centers, assignedCentreId, retryCount])

  const handleConfirm = () => {
    // Outside the radius the button is disabled only while an admin has started
    // enforcement (see `disabled` below). The backend re-checks regardless.
    // Round to 6 decimal places: the backend stores lat/lng as
    // DecimalField(max_digits=9, decimal_places=6) and rejects anything
    // more precise than that.
    if (location) {
      onCapture({
        latitude: Number(location.latitude.toFixed(6)),
        longitude: Number(location.longitude.toFixed(6)),
        accuracy: location.accuracy,
      })
    }
  }

  return (
    <div className="flex flex-col">
      <div className="bg-secondary text-white p-4 flex items-center justify-between">
        <h3 className="font-semibold">Location Verification</h3>
        <button onClick={onCancel} className="p-1 hover:bg-secondary-light rounded transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Status */}
        <div className="text-center">
          {isLoading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm text-neutral-600">Acquiring location...</p>
              {slowHint && (
                <p className="text-xs text-neutral-500 max-w-xs">
                  Still working — this can take a bit longer indoors or with GPS off.
                  Make sure location services are enabled on your device.
                </p>
              )}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 text-red-600">
              <AlertCircle className="w-8 h-8" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          ) : location ? (
            <>
              <MapPin className="w-8 h-8 text-primary mx-auto mb-2" />
              {isWithinGeofence !== null && (
                <div className={`text-sm font-semibold ${isWithinGeofence ? 'text-green-600' : 'text-orange-600'}`}>
                  {isWithinGeofence ? '✓ Within Geofence' : '⚠ Outside Geofence'}
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Location Details */}
        {location && (
          <div className="bg-neutral-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-600">Latitude:</span>
              <span className="font-mono font-semibold">{location.latitude.toFixed(6)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600">Longitude:</span>
              <span className="font-mono font-semibold">{location.longitude.toFixed(6)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600">Accuracy:</span>
              <span className="font-semibold">{Math.round(location.accuracy)}m</span>
            </div>
          </div>
        )}

        {/* Assigned Center */}
        {nearestCenter && (
          <div
            className={`rounded-lg p-4 space-y-2 text-sm ${
              isWithinGeofence ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'
            }`}
          >
            <div className="font-semibold text-neutral-900">{nearestCenter.center.name}</div>
            <div className="flex justify-between text-neutral-600">
              <span>Distance:</span>
              <span className="font-semibold">{Math.round(nearestCenter.distance)}m away</span>
            </div>
            {!isWithinGeofence && (
              <p className="text-xs text-orange-700">
                {enforced
                  ? 'You are outside the allowed area of your assigned center. Check-in and check-out are not allowed from here — move closer to the center.'
                  : 'You are outside the allowed area of your assigned center. Your attendance will be recorded and flagged for review.'}
              </p>
            )}
          </div>
        )}

        {location && assignedCentreId === null && (
          <div className="rounded-lg p-4 text-xs bg-orange-50 border border-orange-200 text-orange-700">
            No training centre is assigned to your profile, so your location cannot be checked against a geofence.
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
            <p className="text-xs text-red-600 mt-2">Check-in requires location access.</p>
            {permissionDenied ? (
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-xs font-semibold text-red-700 underline"
              >
                Reload page
              </button>
            ) : (
              <button
                onClick={() => setRetryCount((c) => c + 1)}
                className="mt-3 text-xs font-semibold text-red-700 underline"
              >
                Try again
              </button>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!location || isLoading || !!error || (enforced && isWithinGeofence === false)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-neutral-400 text-white rounded-lg font-medium transition-colors"
          >
            <Check className="w-4 h-4" />
            Confirm Location
          </button>
        </div>
      </div>
    </div>
  )
}


// import { useEffect, useState } from 'react'
// import { MapPin, Loader2, AlertCircle, Check, X } from 'lucide-react'
// import { centersAPI, attendanceAPI } from '../services/api'

// interface GeolocationCaptureProps {
//   onCapture: (location: { latitude: number; longitude: number; accuracy: number }) => void
//   onCancel: () => void
// }

// // Calculate distance between two coordinates in meters
// function haversineDistance(
//   lat1: number,
//   lon1: number,
//   lat2: number,
//   lon2: number
// ): number {
//   const R = 6371000 // Earth's radius in meters
//   const dLat = ((lat2 - lat1) * Math.PI) / 180
//   const dLon = ((lon2 - lon1) * Math.PI) / 180
//   const a =
//     Math.sin(dLat / 2) * Math.sin(dLat / 2) +
//     Math.cos((lat1 * Math.PI) / 180) *
//       Math.cos((lat2 * Math.PI) / 180) *
//       Math.sin(dLon / 2) *
//       Math.sin(dLon / 2)
//   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
//   return R * c
// }

// export default function GeolocationCapture({ onCapture, onCancel }: GeolocationCaptureProps) {
//   const [location, setLocation] = useState<{ latitude: number; longitude: number; accuracy: number } | null>(null)
//   const [error, setError] = useState<string | null>(null)
//   const [isLoading, setIsLoading] = useState(true)
//   const [isWithinGeofence, setIsWithinGeofence] = useState<boolean | null>(null)
//   const [nearestCenter, setNearestCenter] = useState<any>(null)
//   const [centers, setCenters] = useState<any[]>([])
//   const [slowHint, setSlowHint] = useState(false)
//   const [retryCount, setRetryCount] = useState(0)
//   const [permissionDenied, setPermissionDenied] = useState(false)
//   // Admin-controlled: true only while an admin has started geofence enforcement.
//   const [enforced, setEnforced] = useState(false)

//   useEffect(() => {
//     // Load real training centres from the backend so the geofence preview
//     // reflects actual data (final geofence enforcement always happens
//     // server-side in the check-in/check-out endpoints).
//     // centersAPI.list().then((res) => setCenters(res.data.results || res.data)).catch(() => setCenters([]))
//     centersAPI.listAll().then((res) => setCenters(res.data.results || res.data)).catch(() => setCenters([]))
//     // Whether outside-radius punches are blocked is an admin setting; the server
//     // enforces it regardless — this only decides if the button is disabled.
//     attendanceAPI.getGeofencePolicy().then((res) => setEnforced(!!res.data.is_enforced_now)).catch(() => setEnforced(false))
//   }, [])

//   useEffect(() => {
//     if (!navigator.geolocation) {
//       setError('Geolocation is not supported by your browser')
//       setIsLoading(false)
//       return
//     }
//     setIsLoading(true)
//     setError(null)
//     setPermissionDenied(false)

//     // A high-accuracy GPS fix can take a while indoors. Show a "still
//     // working" hint after 6s, and give up after 15s with an actionable
//     // error instead of spinning forever.
//     const slowTimer = setTimeout(() => setSlowHint(true), 6000)

//     const handlePosition = (position: GeolocationPosition) => {
//       const { latitude, longitude, accuracy } = position.coords
//       const currentLocation = { latitude, longitude, accuracy }
//       setLocation(currentLocation)
//       setIsLoading(false)
//       setError(null)
//       setSlowHint(false)

//       if (centers.length > 0) {
//         const centerDistances = centers.map((center) => ({
//           center,
//           radius: center.geofence_radius_m || 500,
//           distance: haversineDistance(latitude, longitude, Number(center.latitude), Number(center.longitude)),
//         }))

//         const closest = centerDistances.sort((a, b) => a.distance - b.distance)[0]
//         setNearestCenter(closest)
//         setIsWithinGeofence(closest ? closest.distance <= closest.radius : null)
//       }
//     }

//     const handleError = (err: GeolocationPositionError) => {
//       if (err.code === err.PERMISSION_DENIED) {
//         setPermissionDenied(true)
//         setError('Location access is blocked for this site. Enable it in your browser/device settings, then reload the page — clicking "Try again" alone won\'t re-open the permission prompt once it has been denied.')
//       } else if (err.code === err.POSITION_UNAVAILABLE) {
//         setError('Location information is unavailable. Move to an open area and try again.')
//       } else if (err.code === err.TIMEOUT) {
//         setError('Timed out getting your location. Check that GPS/location services are on and try again.')
//       } else {
//         setError('An error occurred while retrieving location.')
//       }
//       setIsLoading(false)
//       setSlowHint(false)
//     }

//     // maximumAge allows a recent cached fix (much faster) instead of always
//     // forcing a brand-new high-accuracy reading; timeout stops it hanging.
//     const watchId = navigator.geolocation.watchPosition(handlePosition, handleError, {
//       enableHighAccuracy: true,
//       maximumAge: 10000,
//       timeout: 15000,
//     })

//     return () => {
//       navigator.geolocation.clearWatch(watchId)
//       clearTimeout(slowTimer)
//     }
//   }, [centers, retryCount])

//   const handleConfirm = () => {
//     // Outside the radius the button is disabled only while an admin has started
//     // enforcement (see `disabled` below). The backend re-checks regardless.
//     // Round to 6 decimal places: the backend stores lat/lng as
//     // DecimalField(max_digits=9, decimal_places=6) and rejects anything
//     // more precise than that.
//     if (location) {
//       onCapture({
//         latitude: Number(location.latitude.toFixed(6)),
//         longitude: Number(location.longitude.toFixed(6)),
//         accuracy: location.accuracy,
//       })
//     }
//   }

//   return (
//     <div className="flex flex-col">
//       <div className="bg-secondary text-white p-4 flex items-center justify-between">
//         <h3 className="font-semibold">Location Verification</h3>
//         <button onClick={onCancel} className="p-1 hover:bg-secondary-light rounded transition-colors">
//           <X className="w-5 h-5" />
//         </button>
//       </div>

//       <div className="p-6 space-y-6">
//         {/* Status */}
//         <div className="text-center">
//           {isLoading ? (
//             <div className="flex flex-col items-center gap-2">
//               <Loader2 className="w-8 h-8 text-primary animate-spin" />
//               <p className="text-sm text-neutral-600">Acquiring location...</p>
//               {slowHint && (
//                 <p className="text-xs text-neutral-500 max-w-xs">
//                   Still working — this can take a bit longer indoors or with GPS off.
//                   Make sure location services are enabled on your device.
//                 </p>
//               )}
//             </div>
//           ) : error ? (
//             <div className="flex flex-col items-center gap-2 text-red-600">
//               <AlertCircle className="w-8 h-8" />
//               <p className="text-sm font-medium">{error}</p>
//             </div>
//           ) : location ? (
//             <>
//               <MapPin className="w-8 h-8 text-primary mx-auto mb-2" />
//               {isWithinGeofence !== null && (
//                 <div className={`text-sm font-semibold ${isWithinGeofence ? 'text-green-600' : 'text-orange-600'}`}>
//                   {isWithinGeofence ? '✓ Within Geofence' : '⚠ Outside Geofence'}
//                 </div>
//               )}
//             </>
//           ) : null}
//         </div>

//         {/* Location Details */}
//         {location && (
//           <div className="bg-neutral-50 rounded-lg p-4 space-y-2 text-sm">
//             <div className="flex justify-between">
//               <span className="text-neutral-600">Latitude:</span>
//               <span className="font-mono font-semibold">{location.latitude.toFixed(6)}</span>
//             </div>
//             <div className="flex justify-between">
//               <span className="text-neutral-600">Longitude:</span>
//               <span className="font-mono font-semibold">{location.longitude.toFixed(6)}</span>
//             </div>
//             <div className="flex justify-between">
//               <span className="text-neutral-600">Accuracy:</span>
//               <span className="font-semibold">{Math.round(location.accuracy)}m</span>
//             </div>
//           </div>
//         )}

//         {/* Nearest Center */}
//         {nearestCenter && (
//           <div
//             className={`rounded-lg p-4 space-y-2 text-sm ${
//               isWithinGeofence ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'
//             }`}
//           >
//             <div className="font-semibold text-neutral-900">{nearestCenter.center.name}</div>
//             <div className="flex justify-between text-neutral-600">
//               <span>Distance:</span>
//               <span className="font-semibold">{Math.round(nearestCenter.distance)}m away</span>
//             </div>
//             {!isWithinGeofence && (
//               <p className="text-xs text-orange-700">
//                 {enforced
//                   ? 'You are outside the allowed area of your nearest center. Check-in and check-out are not allowed from here — move closer to the center.'
//                   : 'You are outside the allowed area of your nearest center. Your attendance will be recorded and flagged for review.'}
//               </p>
//             )}
//           </div>
//         )}

//         {/* Error Alert */}
//         {error && (
//           <div className="bg-red-50 border border-red-200 rounded-lg p-4">
//             <p className="text-sm text-red-700">{error}</p>
//             <p className="text-xs text-red-600 mt-2">Check-in requires location access.</p>
//             {permissionDenied ? (
//               <button
//                 onClick={() => window.location.reload()}
//                 className="mt-3 text-xs font-semibold text-red-700 underline"
//               >
//                 Reload page
//               </button>
//             ) : (
//               <button
//                 onClick={() => setRetryCount((c) => c + 1)}
//                 className="mt-3 text-xs font-semibold text-red-700 underline"
//               >
//                 Try again
//               </button>
//             )}
//           </div>
//         )}

//         {/* Buttons */}
//         <div className="flex gap-2">
//           <button
//             onClick={onCancel}
//             className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-lg font-medium transition-colors"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleConfirm}
//             disabled={!location || isLoading || !!error || (enforced && isWithinGeofence === false)}
//             className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-neutral-400 text-white rounded-lg font-medium transition-colors"
//           >
//             <Check className="w-4 h-4" />
//             Confirm Location
//           </button>
//         </div>
//       </div>
//     </div>
//   )
// }